<template>
  <div class="public-market-analysis">
    <!-- Header & Trends -->
    <div class="trends-header mb-5 mt-4 p-4 p-md-5" style="background-color: #f4f8f4; border-radius: 24px;">
       <div class="row align-items-center mb-4">
          <div class="col-12 col-xl-6 mb-4 mb-xl-0">
            <div class="text-uppercase font-weight-bold mb-3 h6" style="color: #1e7b51; letter-spacing: 2px;">Auction Analytics</div>
            <h1 class="font-weight-bold mb-3 h2" style="color: #0b3d2c;">Cardamom Auction Prices & Market Trends</h1>
            <p class="m-0" style="color: #55796a;">Analyze cardamom auction price averages, maximum clearing prices, and traded volumes. Explore real-time historical data from the live market.</p>
          </div>
          
          <div class="col-12 col-xl-6">
              <div class="summary-cards bg-white p-4 p-xl-5 rounded shadow-sm m-0 ml-xl-auto" style="border-radius: 24px !important; max-width: 500px;">
                 <div class="row">
                   <div class="col-6 mb-4">
                      <div class="text-uppercase small font-weight-bold mb-2" style="color: #55796a;">{{ rangeLabelText }} Average (₹/KG)</div>
                      <div class="h4 font-weight-bold mb-0" style="color: #0b3d2c;">{{ formatNumber(summary.latest_average) }}</div>
                   </div>
                   <div class="col-6 mb-4">
                      <div class="text-uppercase small font-weight-bold mb-2" style="color: #55796a;">{{ rangeLabelText }} Max Price</div>
                      <div class="h4 font-weight-bold mb-0" style="color: #0b3d2c;">{{ formatNumber(summary.max_price) }}</div>
                   </div>
                   <div class="col-6">
                      <div class="text-uppercase small font-weight-bold mb-2" style="color: #55796a;">{{ rangeLabelText }} Qty Sold (Kg)</div>
                      <div class="h4 font-weight-bold mb-0" style="color: #0b3d2c;">{{ formatNumber(summary.total_qty_sold) }}</div>
                   </div>
                   <div class="col-6">
                      <div class="text-uppercase small font-weight-bold mb-2" style="color: #55796a;">{{ rangeLabelText }} Sessions</div>
                      <div class="h4 font-weight-bold mb-0" style="color: #0b3d2c;">{{ summary.sessions_count }}</div>
                   </div>
                 </div>
              </div>
          </div>
       </div>
    </div>

    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3">
       <h4 class="font-weight-bold mb-3 mb-md-0" style="color: #0b3d2c;">Select range</h4>
       <div class="range-selector">
           <button class="btn btn-sm rounded-pill px-4 py-2 font-weight-bold mr-2 mb-2" 
              :style="range === 'this_month' ? 'background-color: #0b3d2c; color: white; border: 1px solid #0b3d2c;' : 'background-color: transparent; color: #1e7b51; border: 1px solid #a8e0b9;'" 
              @click="setRange('this_month')">1 month</button>
           <button class="btn btn-sm rounded-pill px-4 py-2 font-weight-bold mr-2 mb-2" 
              :style="range === 'last_3_months' ? 'background-color: #0b3d2c; color: white; border: 1px solid #0b3d2c;' : 'background-color: transparent; color: #1e7b51; border: 1px solid #a8e0b9;'" 
              @click="setRange('last_3_months')">3 months</button>
           <button class="btn btn-sm rounded-pill px-4 py-2 font-weight-bold mr-2 mb-2" 
              :style="range === 'last_6_months' ? 'background-color: #0b3d2c; color: white; border: 1px solid #0b3d2c;' : 'background-color: transparent; color: #1e7b51; border: 1px solid #a8e0b9;'" 
              @click="setRange('last_6_months')">6 months</button>
           <button class="btn btn-sm rounded-pill px-4 py-2 font-weight-bold mr-2 mb-2" 
              :style="range === 'this_year_so_far' ? 'background-color: #0b3d2c; color: white; border: 1px solid #0b3d2c;' : 'background-color: transparent; color: #1e7b51; border: 1px solid #a8e0b9;'" 
              @click="setRange('this_year_so_far')">1 year</button>
       </div>
    </div>

       <div v-if="isTrendsLoading" class="text-center p-5">
           <i class="fa fa-spinner fa-spin fa-3x text-primary mb-3"></i>
           <p>Loading charts...</p>
       </div>

       <div v-else class="row">
           <div class="col-md-12 mb-4">
               <div class="card border-0 shadow-sm rounded-lg h-100 bg-transparent">
                  <div class="card-body p-0">
                      <h4 class="font-weight-bold" style="color: var(--color-dark);">Average price trend</h4>
                      <p class="text-muted small mb-4">Weighted cardamom averages for the selected window.</p>
                      <apexchart type="line" height="300" :options="priceChartOptions" :series="priceSeries"></apexchart>
                  </div>
               </div>
           </div>
           <div class="col-md-12 mb-4">
               <div class="card border-0 shadow-sm rounded-lg h-100 bg-transparent">
                  <div class="card-body p-0">
                      <h4 class="font-weight-bold mt-4" style="color: var(--color-dark);">Quantity sold trend</h4>
                      <p class="text-muted small mb-4">Total cardamom quantity sold (kg) across the range.</p>
                      <apexchart type="area" height="300" :options="qtyChartOptions" :series="qtySeries"></apexchart>
                  </div>
               </div>
           </div>
       </div>

    <!-- Past Auctions List -->
    <div class="mb-4">
        <h3 class="font-weight-bold mb-1" style="color: var(--color-dark);">Recent Auction Results</h3>
        <p class="text-muted m-0">Get in touch with us for more insights.</p>
    </div>

    <div v-if="isAuctionsLoading" class="p-5 text-center bg-white rounded-lg shadow-sm border mb-4">
        <i class="fa fa-spinner fa-spin fa-3x text-primary mb-3"></i>
        <h4 class="text-muted font-weight-bold">Loading Recent Auctions...</h4>
    </div>

    <div v-else-if="auctions.length === 0" class="p-5 text-center bg-white rounded-lg shadow-sm border">
        <h4 class="text-muted font-weight-bold">No Past Auctions</h4>
    </div>

    <div v-else class="auction-cards-container row">
        <!-- Exact Same Layout as BuyerPastAuctions for individual cards -->
        <div class="col-md-6 mb-4" v-for="(auction, index) in auctions" :key="index">
            <div class="card premium-auction-card h-100 border-0 shadow-sm rounded-lg" style="background-color: #f7f9f7;">
                <div class="card-body p-4 d-flex flex-column">
                    <!-- Top Info Panel -->
                    <div class="mb-4 border-bottom pb-3">
                        <div class="d-flex align-items-center mb-3">
                            <span class="badge badge-secondary px-3 py-2 rounded-pill font-weight-bold"><i class="fa fa-check-circle mr-1"></i>ENDED</span>
                        </div>
                        <h3 class="auction-name font-weight-bold" style="color: var(--color-dark);">{{ auction.name || 'Historic Cardamom Auction' }}</h3>
                        <p class="text-muted m-0">{{ auction.description || 'Archived catalog from ended auction session.' }}</p>
                    </div>

                    <!-- Meta Grid -->
                    <div class="auction-meta-grid mb-4" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px;">
                        <div class="meta-item bg-white shadow-sm p-2 rounded text-center">
                            <span class="d-block text-muted small text-uppercase mb-1">Session Date</span>
                            <span class="font-weight-bold text-dark" style="font-size: 0.95rem;"><i class="fa fa-calendar-alt mr-1 text-secondary"></i>{{ formatDateDMY(auction.schedule_start) }}</span>
                        </div>
                        <div class="meta-item bg-white shadow-sm p-2 rounded text-center">
                            <span class="d-block text-muted small text-uppercase mb-1">Final Average</span>
                            <span class="font-weight-bold text-success" style="font-size: 0.95rem;"><i class="fa fa-rupee-sign mr-1"></i>{{ auction.average || '0.00' }}</span>
                        </div>
                        <div class="meta-item bg-white shadow-sm p-2 rounded text-center">
                            <span class="d-block text-muted small text-uppercase mb-1">Lots Sold</span>
                            <span class="font-weight-bold text-success" style="font-size: 0.95rem;"><i class="fa fa-check-circle mr-1"></i>{{ auction.lots_sold }}/{{ auction.total_lots }}</span>
                        </div>
                    </div>

                    <!-- Grade Matrix Panel -->
                    <div class="mt-auto">

                        <!-- Grade Breakdown Table -->
                        <div class="grade-table-wrapper table-responsive bg-white rounded shadow-sm">
                            <table class="table table-sm m-0">
                                <!-- Rate Header -->
                                <thead class="bg-light">
                                    <tr>
                                        <th class="p-2 border-0">Grade</th>
                                        <th class="text-center p-2 border-0">Max (₹)</th>
                                        <th class="text-center p-2 border-0">Avg (₹)</th>
                                        <th class="text-center p-2 border-0">Min (₹)</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr v-for="(grade, idx) in parseGradeData(auction.gradedata)" :key="idx" class="border-bottom">
                                        <td class="align-middle p-2" :style="{ borderLeft: '6px solid ' + (grade.color_code || '#e2e8f0'), paddingLeft: '12px' }">
                                            <span class="font-weight-bold text-dark">{{ grade.name.includes(' ') ? grade.name.substring(grade.name.indexOf(' ') + 1) : grade.name }}</span>
                                        </td>
                                        
                                        <!-- Rate Metrics -->
                                        <td class="text-center align-middle font-weight-bold p-2">{{ grade.highest }}</td>
                                        <td class="text-center align-middle font-weight-bold text-success p-2">{{ grade.average }}</td>
                                        <td class="text-center align-middle font-weight-bold text-muted p-2">{{ grade.lowest }}</td>
                                    </tr>
                                </tbody>

                                <tfoot>
                                    <tr>
                                        <td colspan="2" class="font-weight-bold align-middle p-3">Final Average</td>
                                        <td colspan="2" class="text-center font-weight-bold text-white bg-success p-3">
                                            ₹ {{ auction.average || '0.00' }}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
export default {
    data() {
        return {
            isAuctionsLoading: true,
            isTrendsLoading: true,
            auctions: [],
            range: 'this_month',
            summary: {
                latest_average: 0,
                max_price: 0,
                total_qty_sold: 0,
                sessions_count: 0
            },
            priceSeries: [],
            qtySeries: [],
            chartDates: [],
            chartOptionsBase: {
                chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit', background: 'transparent' },
                dataLabels: { enabled: false },
                stroke: { curve: 'smooth', width: 3 },
                xaxis: { type: 'category', labels: { style: { colors: '#64748b', fontSize: '11px' } } },
                grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
                markers: { size: 4, hover: { size: 6 } },
                tooltip: { theme: 'light', x: { show: true } }
            }
        }
    },
    computed: {
        rangeLabelText() {
            switch(this.range) {
                case 'this_month': return '1 Month';
                case 'last_3_months': return '3 Month';
                case 'last_6_months': return '6 Month';
                case 'this_year_so_far': return 'YTD';
                default: return 'Period';
            }
        },
        priceChartOptions() {
            return {
                ...this.chartOptionsBase,
                colors: ['#3b82f6'],
                xaxis: { ...this.chartOptionsBase.xaxis, categories: this.chartDates },
                yaxis: { labels: { formatter: (value) => value.toLocaleString('en-IN'), style: { colors: '#64748b' } } }
            }
        },
        qtyChartOptions() {
            return {
                ...this.chartOptionsBase,
                colors: ['#a16207'],
                fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] } },
                xaxis: { ...this.chartOptionsBase.xaxis, categories: this.chartDates },
                yaxis: { labels: { formatter: (value) => value.toLocaleString('en-IN'), style: { colors: '#64748b' } } }
            }
        }
    },
    created() {
        this.fetchAuctions();
        this.fetchTrends();
    },
    methods: {
        setRange(range) {
            this.range = range;
            this.fetchTrends();
        },
        formatNumber(num) {
            return Number(num).toLocaleString('en-IN');
        },
        formatDateDMY(dateStr) {
            if (!dateStr) return '-';
            let d = dateStr.split(" ")[0].split("-");
            if (d.length === 3) return `${d[2]}-${d[1]}-${d[0]}`;
            return dateStr;
        },
        parseGradeData(gradedata) {
            if (!gradedata) return [];
            let parsed = [];
            if (typeof gradedata === 'string') {
                try { gradedata = JSON.parse(gradedata); } catch (e) { return []; }
            }
            if (Array.isArray(gradedata)) {
                gradedata.forEach(item => {
                    let key = Object.keys(item)[0];
                    if (key) {
                        let gradeObj = item[key];
                        gradeObj.name = key;
                        parsed.push(gradeObj);
                    }
                });
            }
            return parsed;
        },
        fetchAuctions() {
            this.isAuctionsLoading = true;
            axios.get('/api/public/market-analysis')
                .then(res => {
                    this.auctions = res.data;
                })
                .catch(err => console.error(err))
                .finally(() => this.isAuctionsLoading = false);
        },
        fetchTrends() {
            this.isTrendsLoading = true;
            axios.get('/api/public/market-trends?range=' + this.range)
                .then(res => {
                    this.summary = res.data.summary;
                    let dates = res.data.trends.map(t => {
                        let d = new Date(t.date);
                        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    });
                    this.priceSeries = [{ name: 'Average Price (₹)', data: res.data.trends.map(t => Number(t.average_price).toFixed(0)) }];
                    this.qtySeries = [{ name: 'Quantity Sold (Kg)', data: res.data.trends.map(t => Number(t.total_qty_sold).toFixed(0)) }];
                    this.chartDates = dates;
                })
                .catch(err => console.error(err))
                .finally(() => this.isTrendsLoading = false);
        }
    }
}
</script>
